/**
 * Money as the API sends it: an integer count of minor units plus an ISO 4217
 * code. Amounts are never converted or relabelled on the client — the display
 * currency preference must not change what a number means.
 */
export type Money = { amount_minor: number; currency: string };

export type FormatMoneyOptions = {
  /** BCP 47 locale; defaults to the active UI language's money locale. */
  locale?: string;
  /** Short form for metric tiles, e.g. "KES 1.2M". */
  compact?: boolean;
  /** Always show a sign, e.g. "+KES 300.00" for variances. */
  signed?: boolean;
};

const fractionDigitsCache = new Map<string, number>();

/** Minor-unit exponent for a currency (KES/USD/EUR → 2, UGX → 0). */
export function minorUnitDigits(currency: string): number {
  const code = currency.toUpperCase();
  const cached = fractionDigitsCache.get(code);
  if (cached !== undefined) return cached;
  let digits: number;
  try {
    digits =
      new Intl.NumberFormat('en', { style: 'currency', currency: code }).resolvedOptions()
        .maximumFractionDigits ?? 2;
  } catch {
    digits = 2;
  }
  fractionDigitsCache.set(code, digits);
  return digits;
}

/**
 * Exact decimal string for an integer minor amount ("123456", 2 → "1234.56").
 * Built from the integer's digits, so no floating-point rounding can creep in.
 */
export function minorToDecimalString(amountMinor: number, digits: number): string {
  if (!Number.isSafeInteger(amountMinor)) {
    throw new RangeError(`amount_minor must be a safe integer, got ${amountMinor}`);
  }
  const negative = amountMinor < 0;
  const raw = Math.abs(amountMinor)
    .toString()
    .padStart(digits + 1, '0');
  const whole = digits ? raw.slice(0, -digits) : raw;
  const fraction = digits ? raw.slice(-digits) : '';
  return `${negative ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
}

export function formatMoney(money: Money, options: FormatMoneyOptions = {}): string {
  const currency = money.currency.toUpperCase();
  const digits = minorUnitDigits(currency);
  const decimal = minorToDecimalString(money.amount_minor, digits);
  const locale = options.locale ?? 'en-KE';

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      signDisplay: options.signed ? 'exceptZero' : 'auto',
      ...(options.compact
        ? { notation: 'compact', maximumFractionDigits: 1 }
        : { minimumFractionDigits: digits, maximumFractionDigits: digits }),
    });
    // Compact notation rounds anyway; the exact string path keeps full amounts exact.
    return options.compact
      ? formatter.format(Number(decimal))
      : formatter.format(decimal as Intl.StringNumericLiteral);
  } catch {
    // Unknown currency code from the server: still show the number honestly.
    return `${currency} ${decimal}`;
  }
}

/** Locale used for numbers and money in each UI language. */
export function moneyLocale(language: string): string {
  if (language === 'sw') return 'sw-KE';
  // Arabic UI keeps Latin digits for amounts: field teams copy figures into
  // M-Pesa and bank apps that expect them.
  if (language === 'ar') return 'ar-u-nu-latn';
  return 'en-KE';
}

/**
 * Parse an amount typed in major units ("1,500.50") into integer minor units,
 * exactly (string arithmetic, no floats). Null if it is not a valid amount for
 * the currency's precision.
 */
export function decimalToMinor(input: string, currency: string): number | null {
  const digits = minorUnitDigits(currency);
  const text = input.replace(/[\s,]/g, '');
  const match = /^(\d+)(?:\.(\d*))?$/.exec(text);
  if (!match) return null;
  const fraction = match[2] ?? '';
  if (fraction.length > digits) return null;
  const minor = Number(`${match[1]}${fraction.padEnd(digits, '0')}`);
  return Number.isSafeInteger(minor) ? minor : null;
}
